import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Box,
  IconButton,
  FormControlLabel,
  Checkbox,
  MenuItem,
} from "@mui/material";
import {
  Email,
  Phone,
  Instagram,
} from "@mui/icons-material";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Pagination, Autoplay } from "swiper/modules";
import "./Contact.css";

const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  phone: yup.string().required("Phone number is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  budget: yup.string().required("Budget is required"),
  service: yup.string().required("Service selection is required"),
  customService: yup.string().when("service", {
    is: "Other",
    then: yup.string().required("Please enter your custom service"),
  }),
  message: yup.string().required("Message is required"),
  preferredTime: yup.string().nullable(),
  callMessage: yup.string().nullable(),
});

const services = [
  "Bathroom Renovation",
  "Kitchen Renovation",
  "Custom Home",
  "Other",
];

const Contact = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm({ resolver: yupResolver(schema) });

  const selectedService = watch("service");
  const [loading, setLoading] = useState(false);
  const [scheduleCall, setScheduleCall] = useState(false);

  const timeSlots = [
    "10:00 AM",
    "11:00 AM",
    "12:00 PM",
    "1:00 PM",
    "2:00 PM",
    "3:00 PM",
    "4:00 PM",
    "5:00 PM",
    "6:00 PM",
  ];

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // 🔹 ReCAPTCHA temporarily removed — no validation check now
      if (data.service === "Other" && data.customService) {
        data.service = data.customService;
      }
      delete data.customService;

      const formData = { ...data, scheduleCall };
      if (!scheduleCall) {
        delete formData.preferredTime;
        delete formData.callMessage;
      }

      // const response = await fetch("http://localhost:5000/api/queries", {
      const response = await fetch("https://urbancasabackendfiles.onrender.com/api/queries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("❌ Unexpected response format (Not JSON)");
      }

      const result = await response.json();
      if (result.success) {
        alert("Form submitted successfully!");
        reset();
      } else {
        alert(result.error || "Submission failed.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Failed to submit form.");
    }
    setLoading(false);
  };

  return (
    <Container maxWidth={false} className="contact-container">
      <Swiper
        modules={[Pagination, Autoplay]}
        spaceBetween={10}
        slidesPerView={1}
        autoplay={{ delay: 3000 }}
        pagination={{ clickable: true }}
        className="contact-swiper"
      >
        <SwiperSlide>
          <img src="assets/thumbnail2.jpeg" alt="Slide 1" className="swiper-image" />
        </SwiperSlide>
        <SwiperSlide>
          <img src="assets/thumbnail4.jpeg" alt="Slide 2" className="swiper-image" />
        </SwiperSlide>
        <SwiperSlide>
          <img src="assets/service-sec-1.jpeg" alt="Slide 3" className="swiper-image" />
        </SwiperSlide>
      </Swiper>

      <Box className="contact-us-material-ui">
        <Typography variant="h4" className="contact-title">
          Contact Us
        </Typography>

        <Grid container spacing={6} className="contact-grid">
          <Grid item xs={12} md={4} className="contact-left">
            <Card className="contact-card">
              <Typography variant="h5">Get in Touch</Typography>
              <CardContent className="getintouch-mian-block">
                <Box className="contact-item">
                  <Email />
                  <Typography
                    component="a"
                    href="mailto:urbancasaau@gmail.com"
                    sx={{
                      fontSize: {
                        xs: "3.6vw",
                        sm: "3vw",
                        md: "2vw",
                        lg: "1.1vw",
                      },
                      textDecoration: "none",
                      color: "inherit",
                      marginLeft: "0.5rem",
                      "&:hover": { textDecoration: "underline", color: "blue" },
                    }}
                  >
                    admin@urbancasa.com.au
                  </Typography>
                </Box>

                <Box className="contact-item">
                  <Phone />
                  <Typography
                    component="a"
                    href="tel:+61498283369"
                    sx={{
                      fontSize: {
                        xs: "3.6vw",
                        sm: "3vw",
                        md: "2vw",
                        lg: "1.1vw",
                      },
                      textDecoration: "none",
                      color: "inherit",
                      marginLeft: "0.5rem",
                      "&:hover": { textDecoration: "underline", color: "blue" },
                    }}
                  >
                    +61 498 283 369
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            <Box className="social-icons">
              <IconButton
                className="social-btn"
                component="a"
                href="https://www.instagram.com/urban.casa13?igsh=aHFwcGM3MDFoNHZx"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Instagram />
              </IconButton>
            </Box>
          </Grid>

          <Grid item xs={12} md={4} className="contact-right">
            <Card className="form-card">
              <CardContent>
                <Typography variant="h6" className="form-title">
                  Have a project in mind?
                </Typography>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Name"
                        {...register("name")}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        className="Contact-urban-casa-text-fields"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Phone Number"
                        {...register("phone")}
                        error={!!errors.phone}
                        helperText={errors.phone?.message}
                        className="Contact-urban-casa-text-fields"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Email"
                        {...register("email")}
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        className="Contact-urban-casa-text-fields"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Budget"
                        {...register("budget")}
                        error={!!errors.budget}
                        helperText={errors.budget?.message}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Service"
                        select
                        sx={{ width: { xs: "60vw", sm: "40vw", lg: "20vw" } }}
                        fullWidth
                        {...register("service")}
                        error={!!errors.service}
                        helperText={errors.service?.message}
                        className="Contact-urban-casa-text-fields"
                      >
                        {services.map((service) => (
                          <MenuItem key={service} value={service}>
                            {service}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    {selectedService === "Other" && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Enter Custom Service"
                          fullWidth
                          {...register("customService")}
                          error={!!errors.customService}
                          helperText={errors.customService?.message}
                          className="Contact-urban-casa-text-fields"
                        />
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <FormControlLabel
                        sx={{ width: { xs: "60vw", lg: "20vw" } }}
                        control={
                          <Checkbox
                            checked={scheduleCall}
                            onChange={(e) => setScheduleCall(e.target.checked)}
                          />
                        }
                        label="Schedule a Call"
                      />
                    </Grid>

                    {scheduleCall && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            select
                            label="Preferred Time"
                            fullWidth
                            sx={{ width: { xs: "60vw", lg: "12vw" } }}
                            {...register("preferredTime")}
                            error={!!errors.preferredTime}
                            helperText={errors.preferredTime?.message}
                          >
                            {timeSlots.map((time) => (
                              <MenuItem key={time} value={time}>
                                {time}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Call Message"
                            fullWidth
                            sx={{ width: { xs: "60vw", lg: "20vw" } }}
                            multiline
                            rows={4}
                            {...register("callMessage")}
                            error={!!errors.callMessage}
                            helperText={errors.callMessage?.message}
                          />
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12}>
                      <TextField
                        label="Message"
                        fullWidth
                        multiline
                        rows={4}
                        sx={{ width: "20vw" }}
                        {...register("message")}
                        error={!!errors.message}
                        helperText={errors.message?.message}
                        className="Contact-urban-casa-text-fields"
                      />
                    </Grid>
                  </Grid>

                  {/* 🚫 ReCAPTCHA temporarily removed */}

                  <Button
                    type="submit"
                    variant="contained"
                    className="send-btn"
                    disabled={loading}
                  >
                    {loading ? "Sending..." : "Send"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default Contact;
